/**
 * Implementation of the class PlanInvoice and useful classes, functions or
 * constants, to manage the details of invoices retrieved from the backend.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */

import { logger } from '@/config/logger'
import {
  AccountUtil,
  CompanyConsumption,
  CurrencyPlace,
  InvoiceType,
  LogTag,
  SubscriptionFrequency,
  TextUtil,
  TierType,
  Util,
} from '@amplify3/duplo-back-end'
import {
  getModuleDisplayName,
  STORAGE_SERVICE_NAME,
} from '../../components/ModuleConfig/ModuleConfigList'

const OneDayInMs = 1000 * 60 * 60 * 24
const SEARCH_YEAR_LIMIT = 2000

function getPeriod(apiInvoiceLineData: any): [Date, Date, Date] {
  const periodStart = new Date(apiInvoiceLineData.period.start * 1000)
  const periodEnd = new Date(apiInvoiceLineData.period.end * 1000 - OneDayInMs)
  const nextPeriod = new Date(apiInvoiceLineData.period.end * 1000)

  return [periodStart, periodEnd, nextPeriod]
}

/**
 * Return a given Date with reset time.
 * @param date The date to return, without taking into account ites time.
 * @returns The new Date.
 */
function toStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export class TaskDetail {
  moduleName: string
  serviceName: string
  trackProcessedCount: number = 0
  consumedCreditCount: number = 0
  consumedOffPackageCreditCount: number = 0

  constructor(serviceName: string) {
    this.serviceName = serviceName
    this.moduleName = getModuleDisplayName(serviceName)
  }
}

export class Kpi {
  periodStart: Date = new Date(0)
  periodEnd: Date = new Date(0)
  taskDetailList: Record<string, TaskDetail> = {}
  allTrackProcessedCount: number = 0

  constructor(startDate: Date, endDate: Date) {
    this.periodStart = startDate
    this.periodEnd = endDate
  }
}

export class PlanInvoice {
  tier: TierType = TierType.Unknown
  planPrice: number = 0
  totalCreditUsedCount: number = 0
  baseCost: number = 0
  baseCreditMax: number = 0
  baseCreditUsedCount: number = 0
  offPackageCreditUsedCount: number | undefined = undefined
  offPackageCreditMax: number | undefined = undefined
  offPackageUnitCost: number | undefined = undefined
  offPackageCost: number | undefined = undefined
  paidCreditCount: number = 0
  totalCost: number = 0
  currency: string = '$'
  currencyPlace: CurrencyPlace = CurrencyPlace.Before
  frequency: SubscriptionFrequency | undefined = undefined
  periodStart: Date
  periodEnd: Date
  periodCreditUsedCount: number = 0
  nextPeriod: Date
  offPackagePeriodStart: Date
  offPackagePeriodEnd: Date
  nextOffPackagePeriod: Date
  offPackagePeriodCreditUsedCount: number = 0

  kpiHistoryList: Kpi[] = []

  constructor() {
    const epoch = new Date(0)
    this.periodStart = epoch
    this.periodEnd = epoch
    this.nextPeriod = epoch
    this.offPackagePeriodStart = epoch
    this.offPackagePeriodEnd = epoch
    this.nextOffPackagePeriod = epoch
  }

  debugLog() {
    logger.debug(LogTag.BillingPage, '---')
    logger.debug(LogTag.BillingPage, 'Invoice values:')
    logger.debug(LogTag.BillingPage, '   tier:', this.tier)
    logger.debug(LogTag.BillingPage, '   plan price:', this.planPrice)
    logger.debug(
      LogTag.BillingPage,
      '   total number of used credits:',
      this.totalCreditUsedCount
    )
    logger.debug(
      LogTag.BillingPage,
      '   number of used credits this period:',
      this.periodCreditUsedCount
    )
    logger.debug(
      LogTag.BillingPage,
      '   number of used off-package credits this period:',
      this.offPackageCreditUsedCount
    )
    logger.debug(
      LogTag.BillingPage,
      '   base max number of credits:',
      this.baseCreditMax
    )
    logger.debug(
      LogTag.BillingPage,
      '   base number of used credits:',
      this.baseCreditUsedCount
    )
    logger.debug(
      LogTag.BillingPage,
      '   off-package number of used credits:',
      this.offPackageCreditUsedCount
    )
    logger.debug(
      LogTag.BillingPage,
      '   off-package max number of credits:',
      this.offPackageCreditMax
    )
    logger.debug(
      LogTag.BillingPage,
      '   off-package credit unit cost:',
      this.offPackageUnitCost
    )
    logger.debug(
      LogTag.BillingPage,
      '   off-package total cost:',
      this.offPackageCost
    )
    logger.debug(
      LogTag.BillingPage,
      '   paid credit count:',
      this.paidCreditCount
    )
    logger.debug(LogTag.BillingPage, '   total cost:', this.totalCost)
    logger.debug(LogTag.BillingPage, '   currency:', this.currency)
    logger.debug(LogTag.BillingPage, '   currency place:', this.currencyPlace)
    logger.debug(LogTag.BillingPage, '   frequency:', this.frequency)
    logger.debug(LogTag.BillingPage, '   period start:', this.periodStart)
    logger.debug(LogTag.BillingPage, '   period end:', this.periodEnd)
    logger.debug(LogTag.BillingPage, '   list of KPI:', this.kpiHistoryList)
    logger.debug(LogTag.BillingPage, '---')
  }

  /**
   * Instantiate an PlanInvoice instance from an API response.
   *
   * cf. Stripe API reference: https://docs.stripe.com/api/invoices/object
   *
   * Enterprise accounts are not managed by Stripe. In that case,
   * the current period starts with the first day of the current of month,
   * and ends with the last day of that month.
   *
   * @param apiDataResponse
   * @returns
   */
  static fromUserInvoiceApi(
    companyConsumption: CompanyConsumption | undefined,
    companyKpi: any,
    apiPlanInvoice: any,
    apiOffPackageInvoice?: any
  ): PlanInvoice | undefined {
    const invoice = new PlanInvoice()

    invoice.tier = AccountUtil.getCompanyTier(companyConsumption)
    invoice.totalCreditUsedCount =
      AccountUtil.getConsumption(companyConsumption)
    invoice.baseCreditUsedCount =
      AccountUtil.getPlanCreditCount(companyConsumption)
    invoice.paidCreditCount = AccountUtil.getPaidCreditCount(companyConsumption)

    if (TierType.Subscription == invoice.tier) {
      invoice.offPackageCreditMax =
        companyConsumption?.overdue_credits_limit ?? 0
      invoice.offPackageCreditUsedCount =
        AccountUtil.getOffPackageCreditCount(companyConsumption)
      invoice.offPackageUnitCost =
        (apiOffPackageInvoice?.lines.data[0].price.unit_amount_decimal ?? 0) *
        10
      invoice.offPackageCost =
        invoice.offPackageUnitCost * invoice.offPackageCreditUsedCount
    }

    if (apiPlanInvoice) {
      ;[invoice.currency, invoice.currencyPlace] = TextUtil.currencySymbol(
        apiPlanInvoice.currency
      )

      if (TierType.PayAsYouGo == invoice.tier) {
        invoice.planPrice =
          apiPlanInvoice.lines.data[0].price.unit_amount_decimal * 10
        invoice.baseCost = invoice.planPrice * invoice.totalCreditUsedCount
      } else {
        invoice.planPrice =
          (apiPlanInvoice.lines.data[0].price.unit_amount_decimal ?? 0) / 100
        invoice.baseCost = invoice.planPrice
      }

      const data = apiPlanInvoice.lines.data[0]
      if (data) {
        invoice.frequency = undefined
        if (data.price.recurring) {
          const interval = data.price.recurring.interval
          invoice.frequency =
            interval === 'month'
              ? SubscriptionFrequency.Monthly
              : interval === 'year'
                ? SubscriptionFrequency.Yearly
                : SubscriptionFrequency.Unknown
        }
        const [periodStart, periodEnd, nextPeriod] = getPeriod(data)
        invoice.periodStart = periodStart
        invoice.periodEnd = periodEnd
        invoice.nextPeriod = nextPeriod
      }
    }

    // Compute the total cost for the current period
    const absTotalCost = invoice.baseCost + (invoice.offPackageCost || 0)
    invoice.totalCost =
      TierType.Subscription == invoice.tier &&
      SubscriptionFrequency.Yearly == invoice.frequency
        ? (invoice.offPackageCost ?? 0)
        : (absTotalCost ?? 0)

    // Check period dates
    if (
      Util.isEpoch(invoice.periodStart) &&
      Util.isEpoch(invoice.periodEnd) &&
      Util.isEpoch(invoice.nextPeriod)
    ) {
      const today = new Date()

      // First day of current month
      invoice.periodStart = new Date(today.getFullYear(), today.getMonth(), 1)

      // Last day of current month
      invoice.periodEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)

      // First day of next month
      invoice.nextPeriod = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        1
      )
    }

    invoice.baseCreditMax = AccountUtil.getConsumptionMaxCount(
      companyConsumption,
      apiPlanInvoice
    )

    const offPackageData = apiOffPackageInvoice?.lines.data[0]
    if (offPackageData) {
      const [periodStart, periodEnd, nextPeriod] = getPeriod(offPackageData)
      invoice.offPackagePeriodStart = periodStart
      invoice.offPackagePeriodEnd = periodEnd
      invoice.nextOffPackagePeriod = nextPeriod
    }

    // Compute KPI history list
    if (companyKpi) {
      for (const kpi of companyKpi) {
        const kpiDate = new Date(kpi.date)

        // Update data for the current period
        if (invoice.periodStart <= kpiDate && kpiDate <= invoice.periodEnd) {
          invoice.periodCreditUsedCount += kpi.consumed_credits_package ?? 0
          invoice.offPackagePeriodCreditUsedCount +=
            kpi.consumed_credits_off_package ?? 0
        }

        // Update data of the module of the KPI in the history list
        if (STORAGE_SERVICE_NAME !== kpi.service_name) {
          const kpiInstance = invoice.findKpiByDate(
            kpiDate, // Date of search
            invoice.periodStart // Fallback if not found
          )

          if (!kpiInstance.taskDetailList[kpi.service_name]) {
            kpiInstance.taskDetailList[kpi.service_name] = new TaskDetail(
              kpi.service_name
            )
          }

          const taskDetail = kpiInstance.taskDetailList[kpi.service_name]
          taskDetail.consumedCreditCount += kpi.consumed_credits_package
          taskDetail.consumedOffPackageCreditCount +=
            kpi.consumed_credits_off_package
          taskDetail.trackProcessedCount += kpi.nb_files
          kpiInstance.allTrackProcessedCount += kpi.nb_files
        }
      }
    }

    invoice.debugLog()
    return invoice
  }

  /**
   * Returns the KPI instance of the KPI history list, given a date.
   *
   * @param targetDate
   *    The date which belongs to the KPI to return.
   * @param fallbackDate
   *    The start date used to create a new instance,
   *    if the target date is not found
   *    (typically the start of the current period).
   * @returns
   *    The Kpi instance. If not found, a new instance is created
   *    and added to the list.
   */
  findKpiByDate(targetDate: Date, fallbackDate: Date): Kpi {
    // Search in existing KPI
    let low = 0
    let high = this.kpiHistoryList.length - 1

    targetDate = toStartOfDay(targetDate)

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      const kpi = this.kpiHistoryList[mid]

      if (targetDate < kpi.periodStart) {
        low = mid + 1
      } else if (targetDate > kpi.periodEnd) {
        high = mid - 1
      } else {
        // Instance found, return it.
        return kpi
      }
    }

    // Not found — create and insert a new Kpi with correct period
    const day = fallbackDate.getDate()
    const anchorMonth = fallbackDate.getMonth()
    const anchorYear = fallbackDate.getFullYear()
    const anchorTotalMonths = anchorYear * 12 + anchorMonth

    let offset = 0
    const SEARCH_LIMIT = 500 // Roughly 40 years
    while (offset < SEARCH_LIMIT) {
      const candidateTotalMonths =
        targetDate > fallbackDate
          ? anchorTotalMonths + offset
          : anchorTotalMonths - offset

      const year = Math.floor(candidateTotalMonths / 12)
      const month = candidateTotalMonths % 12

      if (year < 2000) {
        throw new Error(
          `Target date ${targetDate.toISOString()} too far in the past`
        )
      }

      const periodStart = new Date(year, month, day)
      const periodEnd = new Date(year, month + 1, day - 1)

      if (periodStart <= targetDate && targetDate <= periodEnd) {
        const newKpi = new Kpi(periodStart, periodEnd)
        this.kpiHistoryList.splice(low, 0, newKpi)
        return newKpi
      }

      offset++
    }

    throw new Error(
      `Target date ${targetDate.toISOString()} is too far in the past. ` +
        `Must not go before year ${SEARCH_YEAR_LIMIT}.`
    )
  }
}

export function getInvoice(
  consumption: CompanyConsumption | undefined,
  companyKpi: any | undefined,
  apiResponse: any
): PlanInvoice | undefined {
  const tier = AccountUtil.getCompanyTier(consumption)

  let planInvoice = undefined
  let planStart = undefined
  let offPackageStart = undefined
  let apiPlanInvoice = undefined
  let apiOffPkgInvoice = undefined

  for (const invoice of apiResponse?.data?.invoices) {
    if (invoice.lines?.data?.length > 0) {
      const currentStart = invoice.lines.data[0].period.start
      const currentPlanLabel = invoice.lines.data[0].price.lookup_key
      const currentPlan = AccountUtil.invoiceLabelToType(currentPlanLabel)

      switch (currentPlan) {
        case InvoiceType.PayAsYouGo:
          if (TierType.PayAsYouGo == tier) {
            if (planStart == undefined || planStart < currentStart) {
              planStart = currentStart
              apiPlanInvoice = invoice
            }
          }
          break

        case InvoiceType.StarterMonthlyBase:
        case InvoiceType.StarterYearlyBase:
        case InvoiceType.ProMonthlyBase:
        case InvoiceType.ProYearlyBase:
          if (TierType.Subscription == tier) {
            if (planStart == undefined || planStart < currentStart) {
              planStart = currentStart
              apiPlanInvoice = invoice
            }
          }
          break

        case InvoiceType.StarterMonthlyOverPackage:
        case InvoiceType.StarterYearlyOverPackage:
        case InvoiceType.ProMonthlyOverPackage:
        case InvoiceType.ProYearlyOverPackage:
          if (TierType.Subscription == tier) {
            if (offPackageStart == undefined || planStart < currentStart) {
              offPackageStart = currentStart
              apiOffPkgInvoice = invoice
            }
          }

        case InvoiceType.TopUpBase:
          if (TierType.TopUpCreditAimd == tier) {
            if (planStart == undefined || planStart < currentStart) {
              planStart = currentStart
              apiPlanInvoice = invoice
            }
          }
          break

        default:
        // Do nothing
      }
    }
  }

  planInvoice = PlanInvoice.fromUserInvoiceApi(
    consumption,
    companyKpi,
    apiPlanInvoice,
    apiOffPkgInvoice
  )

  return planInvoice
}

export enum InvoiceStatus {
  Unknown = 'unknown',
  Pending = 'pending',
  Paid = 'paid',
}

function stringToInvoiceStatus(str: string): InvoiceStatus {
  switch (str.toLowerCase()) {
    case 'pending':
      return InvoiceStatus.Pending

    case 'paid':
      return InvoiceStatus.Paid

    default:
      return InvoiceStatus.Unknown
  }
}

export function formatInvoiceDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  // Get O-indexed month
  const month = String(date.getMonth() + 1).padStart(2, '0')
  // Get last two digits
  const year = String(date.getFullYear()).slice(-2)
  return `${day}.${month}.${year}`
}

/**
 * A class defining one link to an invoice's PDF.
 */
export class InvoiceLink {
  ref: string | null
  id: string
  number: number | undefined
  creationDate: Date
  pdfLink: string
  status: InvoiceStatus

  constructor(
    invoiceNumber: string | null,
    id: string,
    date: Date,
    pdfLink: string,
    status: InvoiceStatus
  ) {
    this.ref = invoiceNumber
    this.id = id
    this.creationDate = date
    this.pdfLink = pdfLink
    this.status = status

    // Get the number from the reference "CCxxxxxx-nnnn"
    const match = invoiceNumber?.match(/-(\d+)$/)
    this.number = match ? Number(match[1]) : undefined
  }

  /**
   * Instantiate an InvoiceLink instance from an API response.
   *
   * cf. Stripe API reference: https://docs.stripe.com/api/invoices/object
   *
   * @param apiDataResponse
   * @returns
   */
  static fromUserApi(apiDataResponse: any): InvoiceLink[] {
    const invoiceList: InvoiceLink[] = []

    for (const invoiceData of apiDataResponse.data.invoices) {
      const number = invoiceData.number
      const id = invoiceData.id
      const status = stringToInvoiceStatus(invoiceData.status)
      const pdfLink = invoiceData.invoice_pdf
      const creationDate = new Date(invoiceData.created * 1000)
      invoiceList.push(
        new InvoiceLink(number, id, creationDate, pdfLink, status)
      )
    }

    return invoiceList
  }
}
